import React, { useEffect, useState } from "react";
import Select, { components } from "react-select";
import { FieldFeedbackLabel } from "./FieldFeedbackLabel";
import { FormattedMessage } from "react-intl";
import { get } from "lodash";
import classNames from "classnames";
import convertString from "helpers/convertString";

const Checkbox = ({ value, onChange, label }) => {
  return (
    <div className="d-flex cursor-pointer" onClick={() => onChange((e) => !e)}>
      <div
        className="d-flex mr-2"
        style={{
          minWidth: 18,
          height: 18,
          border: `2px solid ${value ? "#7738C7" : "#c1c1c1"}`,
          padding: 1
        }}
      >
        {value && <div className="w-100 h-100" style={{ background: "#7738C7" }} />}
      </div>
      {label}
    </div>
  );
};

const getFieldCSSClasses = (touched, errors) => {
  const classes = ["lms-advanced-select"];
  if (touched && errors) {
    classes.push("is-invalid");
  }

  if (touched && !errors) {
    classes.push("is-valid");
  }

  return classes.join(" ");
};

const AdvancedSelect = ({ ...props }) => {
  const {
    field,
    form,
    label,
    optionsList,
    selectAllOptions = false,
    addComponentToThisOptions,
    addComponentOptionValue,
    ComponentToBeAdded,
    optionLabelProp,
    optionValueProp,
    placeholder,
    onChange,
    isClearable,
    withFeedbackLabel,
    isMulti,
    isDisabled,
    customFeedbackLabel,
    classes,
    valueViewMode,
    showLabel = true,
    formGroup = true,
    customizedOptionFields,
    customKeyFields,
    menuPlacement = "bottom",
    prefix = " ",
    isLoading,
    optionDisableKey,
    customStyle = {},
    menuPosition,
    infoText,
    bottomInfoText,
    closeMenuOnSelect = true
  } = props;
  const { touched, errors } = form;
  const [isSelectAll, setIsSelectAll] = useState(false);

  useEffect(() => {
    if (selectAllOptions) {
      isSelectAll ? handleChange(optionsList) : handleChange([]);
    }
  }, [isSelectAll]);

  const customStyles = {
    control: (styles, state) => ({
      ...styles,
      backgroundColor: isDisabled ? "#f3f6f9" : "white"
    }),
    menu: (provided, state) => ({
      ...provided,
      marginTop: "3px",
      zIndex: 999
    }),
    menuList: (provided, state) => ({
      ...provided,
      maxHeight: "200px"
    }),
    ...customStyle
  };

  // gelen listede seçili eleman yoksa ve liste değiştiyse seçili elemanı kaldırır.
  useEffect(() => {
    if (
      Array.isArray(optionsList) &&
      optionsList.length &&
      field.value !== null &&
      typeof field.value !== "undefined" &&
      field.value.hasOwnProperty(optionValueProp)
    ) {
      let hasInList = optionsList.find(
        (option) => option[optionValueProp] === field.value[optionValueProp]
      );

      if (typeof hasInList === "undefined") {
        form.setFieldValue(field.name, null);
      }
    }
  }, [JSON.stringify(optionsList), field.value]);

  const getAvailableLabel = (option) => {
    let labelVal = "";
    if (Array.isArray(optionLabelProp)) {
      const temp = [];
      for (let el of optionLabelProp) {
        if (option.hasOwnProperty(el) && option[el]) {
          temp.push(option[el]);
        }
      }
      labelVal = temp.join(prefix);
    } else if (option.hasOwnProperty(optionLabelProp)) {
      labelVal = option[optionLabelProp];
    }

    //return <FormattedMessage id={labelVal} />;
    return labelVal;
  };
  const CustomOption = ({ children, ...props }) => {
    return (
      <components.Option {...props} className="d-flex">
        {addComponentToThisOptions?.includes(props.data[addComponentOptionValue]) &&
          ComponentToBeAdded}
        {children}
      </components.Option>
    );
  };

  const handleChange = (option) => {
    // spesific fied value
    if (customizedOptionFields) {
      if (!isMulti) {
        const newOptionObj = {};
        customizedOptionFields.forEach((key, index) => {
          newOptionObj[customKeyFields?.[index] || key] = get(option, key);
        });
        form.setFieldValue(field.name, newOptionObj);
      } else {
        const newOptionArr = [...option].map((o) => {
          const newOptionObj = {};
          customizedOptionFields.forEach((key) => {
            newOptionObj[key] = o[key];
          });
          return newOptionObj;
        });
        form.setFieldValue(field.name, newOptionArr);
      }
    } else {
      form.setFieldValue(field.name, option);
    }

    // üst bileşene onChange ile veri aktarma
    if (typeof onChange === "function") {
      onChange(option);
    }
  };

  return (
    <div className={classNames(formGroup && "form-group", classes && classes)}>
      <div className="d-flex justify-content-between">
        <div className="d-flex">
          {showLabel && label && (
            <label
              className={`${
                valueViewMode ? "border-bottom border-light-success border-1 font-weight-bold" : ""
              }`}
            >
              <FormattedMessage id={label} />
            </label>
          )}
          {infoText && <div className="text-muted ml-2 lms-form-info-text">{infoText}</div>}
        </div>
        {selectAllOptions && (
          <div>
            <Checkbox value={isSelectAll} onChange={setIsSelectAll} label="Hepsini Seç" />
          </div>
        )}
      </div>

      {!valueViewMode ? (
        <>
          <Select
            value={field.value}
            onChange={handleChange}
            menuPlacement={menuPlacement}
            noOptionsMessage={() => "Veri Bulunamadı"}
            components={{ Option: CustomOption }}
            isMulti={isMulti}
            isDisabled={isDisabled}
            options={optionsList}
            isSearchable={true}
            isClearable={isClearable}
            placeholder={<FormattedMessage id={placeholder} />}
            getOptionLabel={(option) => getAvailableLabel(option)}
            getOptionValue={(option) => option[optionValueProp]}
            onBlur={field.onBlur}
            styles={customStyles}
            className={getFieldCSSClasses(get(touched, field.name), get(errors, field.name))}
            id={convertString(label)}
            isLoading={isLoading}
            isOptionDisabled={(option) => option[optionDisableKey]}
            menuPosition={menuPosition}
            closeMenuOnSelect={closeMenuOnSelect}
          />
          {bottomInfoText && (
            <div className="text-muted mt-2 lms-form-info-text">{bottomInfoText}</div>
          )}
          {withFeedbackLabel && (
            <FieldFeedbackLabel
              error={get(errors, field.name)}
              touched={get(touched, field.name)}
              label={<FormattedMessage id={label} />}
              type="select"
              customFeedbackLabel={customFeedbackLabel}
            />
          )}
        </>
      ) : (
        <label>{field.value ? field.value[optionLabelProp] : <> </>}</label>
      )}
    </div>
  );
};

export { AdvancedSelect };
